import { useState, useEffect } from 'react';
import apiClient from '@/lib/api/client';

type CalcMode = 'hours' | 'sqft';

interface Form {
  title: string;
  calcMode: CalcMode;
  // hours mode
  total_hours: number;
  hourly_rate: number;
  // sqft mode
  sq_footage: number;
  rate_per_sqft: number;
  // shared
  notes: string;
  status: string;
}

interface Props {
  jobId: string;
  estimateId?: number | null;
  onClose: () => void;
  onSaved: () => void;
}

const DEFAULT_FORM: Form = {
  title: 'Labour Estimate',
  calcMode: 'hours',
  total_hours: 0,
  hourly_rate: 85,
  sq_footage: 0,
  rate_per_sqft: 0,
  notes: '',
  status: 'draft',
};

export const LabourEstimateModal = ({ jobId, estimateId, onClose, onSaved }: Props) => {
  const [form, setForm] = useState<Form>(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (estimateId) loadEstimate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId]);

  const loadEstimate = async () => {
    try {
      setLoading(true);
      const resp = await apiClient.get(`/labour-estimates/${estimateId}`);
      const d = resp.data?.data ?? resp.data;
      setForm(f => ({
        ...f,
        title: d.title ?? '',
        total_hours: Number(d.total_hours) || 0,
        hourly_rate: Number(d.hourly_rate) || 0,
        notes: d.notes ?? '',
        status: d.status ?? 'draft',
      }));
    } catch {
      setFormError('Failed to load labour estimate.');
    } finally {
      setLoading(false);
    }
  };

  const set = (key: keyof Form, value: string | number) =>
    setForm(f => ({ ...f, [key]: value }));

  // Derived values
  const total = form.calcMode === 'sqft'
    ? form.sq_footage * form.rate_per_sqft
    : form.total_hours * form.hourly_rate;

  const handleSave = async () => {
    if (!form.title.trim()) { setFormError('Title is required.'); return; }
    if (form.calcMode === 'hours' && form.hourly_rate <= 0) { setFormError('Hourly rate must be greater than 0.'); return; }
    if (form.calcMode === 'sqft' && form.rate_per_sqft <= 0) { setFormError('Rate per sq ft must be greater than 0.'); return; }
    setFormError('');
    try {
      setSaving(true);
      // Store sqft calc as: total_hours = sq_footage, hourly_rate = rate_per_sqft
      // so total_hours × hourly_rate = total_amount is preserved for DB integrity
      const payload = form.calcMode === 'sqft'
        ? {
            title: form.title,
            total_hours: form.sq_footage,
            hourly_rate: form.rate_per_sqft,
            total_amount: total,
            notes: form.notes || null,
            status: form.status,
          }
        : {
            title: form.title,
            total_hours: form.total_hours,
            hourly_rate: form.hourly_rate,
            total_amount: total,
            notes: form.notes || null,
            status: form.status,
          };
      if (estimateId) {
        await apiClient.put(`/labour-estimates/${estimateId}`, payload);
      } else {
        await apiClient.post('/labour-estimates', { ...payload, job_id: Number(jobId) });
      }
      onSaved();
      onClose();
    } catch {
      setFormError('Failed to save labour estimate.');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n: number) => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(n);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 overflow-y-auto p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg my-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-black text-gray-900">
            {estimateId ? 'Edit Labour Estimate' : 'New Labour Estimate'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : (
          <div className="p-6 flex flex-col gap-5">
            {formError && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{formError}</div>
            )}
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Title</label>
              <input
                type="text"
                value={form.title}
                onChange={e => set('title', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>

            {/* Calc mode toggle */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Calculation Method</label>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm font-medium">
                <button
                  onClick={() => set('calcMode', 'hours')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 transition-colors ${
                    form.calcMode === 'hours'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="material-symbols-outlined text-base">schedule</span>
                  By Hours
                </button>
                <button
                  onClick={() => set('calcMode', 'sqft')}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 transition-colors border-l border-gray-200 ${
                    form.calcMode === 'sqft'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <span className="material-symbols-outlined text-base">square_foot</span>
                  By Sq Footage
                </button>
              </div>
            </div>

            {/* Inputs */}
            {form.calcMode === 'hours' ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Total Hours</label>
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={form.total_hours}
                    onChange={e => set('total_hours', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 text-sm text-right"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">Hourly Rate ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.50"
                    value={form.hourly_rate}
                    onChange={e => set('hourly_rate', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 text-sm text-right"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Square Footage (sq ft)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.sq_footage}
                    onChange={e => set('sq_footage', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 text-sm text-right"
                    placeholder="e.g. 500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Rate ($/sq ft)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.rate_per_sqft}
                    onChange={e => set('rate_per_sqft', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 text-sm text-right"
                    placeholder="e.g. 2.50"
                  />
                </div>
              </div>
            )}

            {/* Total */}
            <div className="bg-primary/10 rounded-xl px-5 py-4 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Labour Cost</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {form.calcMode === 'hours'
                    ? `${form.total_hours} hrs × ${fmt(form.hourly_rate)}/hr`
                    : `${form.sq_footage.toLocaleString()} sq ft × ${fmt(form.rate_per_sqft)}/sq ft`}
                </p>
              </div>
              <span className="text-3xl font-black text-primary">{fmt(total)}</span>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Notes</label>
              <textarea
                value={form.notes}
                onChange={e => set('notes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Scope, assumptions, exclusions…"
              />
            </div>

            {/* Status (edit only) */}
            {estimateId && (
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">Status</label>
                <select
                  value={form.status}
                  onChange={e => set('status', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-gray-900 text-sm"
                >
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="approved">Approved</option>
                  <option value="declined">Declined</option>
                </select>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : estimateId ? 'Update' : 'Save Estimate'}
          </button>
        </div>
      </div>
    </div>
  );
};
